import React, { useState, useContext, createContext } from 'react';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';

// ─── Group context ────────────────────────────────────────────────────────────
const AccordionContext = createContext(null);

/**
 * Accordion group wrapper — manages which items are open when using value/type.
 * Optional: AccordionItem works standalone without this wrapper.
 *
 * Props:
 *   type         - 'single' (only one open at a time) | 'multiple' (default)
 *   defaultValue - string | string[] — uncontrolled initial open item(s)
 *   value        - string | string[] — controlled open item(s)
 *   onValueChange - callback when open item(s) change
 *   variant      - 'default' | 'flush' | 'card' — passed to children
 */
const Accordion = ({
  type = 'multiple',
  defaultValue,
  value: valueProp,
  onValueChange,
  variant = 'default',
  className,
  children,
  ...props
}) => {
  const [uncontrolled, setUncontrolled] = useState(() => {
    if (!defaultValue) return new Set();
    return new Set(Array.isArray(defaultValue) ? defaultValue : [defaultValue]);
  });

  const controlled = valueProp !== undefined
    ? new Set(Array.isArray(valueProp) ? valueProp : [valueProp])
    : null;

  const openItems = controlled ?? uncontrolled;

  const toggle = (itemValue) => {
    const next = new Set(openItems);
    if (type === 'single') {
      next.clear();
      if (!openItems.has(itemValue)) next.add(itemValue);
    } else {
      if (next.has(itemValue)) next.delete(itemValue);
      else next.add(itemValue);
    }

    if (controlled !== null && onValueChange) {
      onValueChange(type === 'single' ? ([...next][0] ?? null) : [...next]);
    } else {
      setUncontrolled(next);
    }
  };

  const wrapperClasses = clsx(
    variant === 'default' && 'rounded-lg border border-gray-200 divide-y divide-gray-200 overflow-hidden bg-white',
    variant === 'flush' && 'divide-y divide-gray-100',
    variant === 'card' && 'space-y-2',
    className
  );

  return (
    <AccordionContext.Provider value={{ openItems, toggle, variant }}>
      <div className={wrapperClasses} {...props} data-otg-component={props['data-otg-component'] || 'Accordion'}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

// ─── Item ─────────────────────────────────────────────────────────────────────
/**
 * AccordionItem — works standalone or inside an Accordion group.
 *
 * Props:
 *   value        - required when inside an Accordion group
 *   title        - header label (string or ReactNode)
 *   subtitle     - optional secondary line below title
 *   badge        - optional ReactNode rendered beside title (counts, status, etc.)
 *   defaultOpen  - boolean — initial open state when standalone
 *   open         - boolean — controlled open state when standalone
 *   onToggle     - (isOpen: boolean) => void — controlled toggle when standalone
 *   variant      - 'default' | 'flush' | 'card' — overrides group variant
 *   headerClassName - extra classes on the trigger button
 *   bodyClassName   - extra classes on the content wrapper
 */
const AccordionItem = ({
  value,
  title,
  subtitle,
  badge,
  defaultOpen = false,
  open: openProp,
  onToggle,
  children,
  className,
  variant: itemVariant,
  headerClassName,
  bodyClassName,
  ...props
}) => {
  const ctx = useContext(AccordionContext);
  const [localOpen, setLocalOpen] = useState(defaultOpen);

  const isGrouped = ctx !== null && value !== undefined;

  const isOpen = isGrouped
    ? ctx.openItems.has(value)
    : openProp !== undefined
      ? openProp
      : localOpen;

  const handleToggle = () => {
    if (isGrouped) {
      ctx.toggle(value);
    } else if (onToggle) {
      onToggle(!isOpen);
    } else {
      setLocalOpen((prev) => !prev);
    }
  };

  const variant = itemVariant ?? ctx?.variant ?? 'card';

  const itemClasses = clsx(
    variant === 'card' && 'rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden',
    className
  );

  const triggerClasses = clsx(
    'w-full flex items-center justify-between gap-3 text-left transition-colors',
    variant === 'card' && 'px-4 py-3 hover:bg-gray-50',
    variant === 'default' && 'px-4 py-3 hover:bg-gray-50',
    variant === 'flush' && 'px-0 py-3 hover:text-gray-900',
    isOpen && variant !== 'flush' && 'bg-gray-50',
    headerClassName
  );

  const contentClasses = clsx(
    'text-sm text-gray-700',
    variant === 'card' && 'px-4 pb-4',
    variant === 'default' && 'px-4 pb-4 bg-white',
    variant === 'flush' && 'pb-3',
    bodyClassName
  );

  return (
    <div className={itemClasses} {...props} data-otg-component={props['data-otg-component'] || 'AccordionItem'}>
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isOpen}
        className={triggerClasses}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900 leading-snug">
              {title}
            </div>
            {subtitle && (
              <div className="text-xs text-gray-500 mt-0.5 leading-snug">{subtitle}</div>
            )}
          </div>
          {badge && <span className="shrink-0">{badge}</span>}
        </div>
        <ChevronDown
          size={16}
          className={clsx(
            'shrink-0 text-gray-400 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className={contentClasses}>
          {children}
        </div>
      )}
    </div>
  );
};

Accordion.Item = AccordionItem;

export { AccordionItem };
export default Accordion;
